import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";

import Form from "react-bootstrap/Form";
import { Alert, Breadcrumb, Button, Col, Row } from "react-bootstrap";

import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { defaultOrderStatus, OrderStatus } from "../../models/responses/orderStatus";
import { postOrderStatus } from "../../app/services/orderStatusService";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { defaultOrderPageType, OrderPageType } from "../../models/responses/orderPageType";

interface Props { }

const OrderStatusAdd: FC<Props> = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [displayError, setDisplayError] = useState(false);
    const booleanLabel = (value: boolean) => (value ? "Ja" : "Nej");

    const isWaitingOrderStatusPostResponse = useSelector(
        (state: RootState) => state.orderStatus.isWaitingOrderStatusPostResponse
    );

    const [orderStatusData, setOrderStatusData] = useState<OrderStatus>({
        ...defaultOrderStatus,
        orderPageType: { ...defaultOrderPageType }
    });

    const handleAddOrderStatus = () => {
        setDisplayError(false);
        dispatch(postOrderStatus(instance, account, orderStatusData, onSuccess));
    };

    const onSuccess = (orderStatusId: string | null) => {
        if (orderStatusId === null) {
            setDisplayError(true);
            return;
        }

        setTimeout(() => {
            navigate(`/admin/order-status/view/${orderStatusId}`, { relative: "path" });
        }, 500);
    };

    return (
        <div>
            <Breadcrumb>
                <Breadcrumb.Item href="/admin/order-status">Orderstatusar</Breadcrumb.Item>
                <Breadcrumb.Item active>Ny orderstatus</Breadcrumb.Item>
            </Breadcrumb>
            <hr />
            <Form className="w-3">
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Orderstatus *</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatusData.name}
                                onInput={(e) => setOrderStatusData({ ...orderStatusData, name: e.currentTarget.value })}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Sortering</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatusData.sortOrder}
                                onInput={(e) => setOrderStatusData({ ...orderStatusData, sortOrder: parseInt(e.currentTarget.value) })}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Klient-typ</Form.Label>
                            <Form.Select
                                aria-label="Klient-typ"
                                multiple
                                value={orderStatusData.clientTypes ? (Array.isArray(orderStatusData.clientTypes) ? orderStatusData.clientTypes.map(String) : [String(orderStatusData.clientTypes)]) : []}
                                onChange={(e) => {
                                    const selectedOptions = Array.from(e.target.selectedOptions).map(option => parseInt(option.value, 10) as ClientType);
                                    setOrderStatusData({
                                        ...orderStatusData,
                                        clientTypes: selectedOptions.length === 0 ? null : selectedOptions,
                                    });
                                }}
                            >
                                <option value="">Alla</option>
                                <option value={ClientType.Standard}>Standard</option>
                                <option value={ClientType.Catalog}>Katalog</option>
                                <option value={ClientType.Fleet}>Fleet</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Visa i meny</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="showInMenu"
                                    label={booleanLabel(orderStatusData.showInMenu)}
                                    checked={orderStatusData.showInMenu}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            showInMenu: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>ableToExportOrdersInStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="ableToExportOrdersInStatus"
                                    label={booleanLabel(orderStatusData.ableToExportOrdersInStatus)}
                                    checked={orderStatusData.ableToExportOrdersInStatus}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            ableToExportOrdersInStatus: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>aXCallbackStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="axCallbackStatus"
                                    label={booleanLabel(orderStatusData.axCallbackStatus)}
                                    checked={orderStatusData.axCallbackStatus}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            axCallbackStatus: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>requiresCatalogApproval</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="requiresCatalogApproval"
                                    label={booleanLabel(orderStatusData.requiresCatalogApproval)}
                                    checked={orderStatusData.requiresCatalogApproval}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            requiresCatalogApproval: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>catalogCallbackStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="catalogCallbackStatus"
                                    label={booleanLabel(orderStatusData.catalogCallbackStatus)}
                                    checked={orderStatusData.catalogCallbackStatus}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            catalogCallbackStatus: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>catalogDeniedStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="catalogDeniedStatus"
                                    label={booleanLabel(orderStatusData.catalogDeniedStatus)}
                                    checked={orderStatusData.catalogDeniedStatus}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            catalogDeniedStatus: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>orderFinalized</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="orderFinalized"
                                    label={booleanLabel(orderStatusData.orderFinalized)}
                                    checked={orderStatusData.orderFinalized}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            orderFinalized: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>deliveredStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="deliveredStatus"
                                    label={booleanLabel(orderStatusData.deliveredStatus)}
                                    checked={orderStatusData.deliveredStatus}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            deliveredStatus: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>goodsInWarehouseStatus</Form.Label>
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="goodsInWarehouseStatus"
                                    label={booleanLabel(orderStatusData.goodsInWarehouseStatus)}
                                    checked={orderStatusData.goodsInWarehouseStatus}
                                    onChange={(e) =>
                                        setOrderStatusData({
                                            ...orderStatusData,
                                            goodsInWarehouseStatus: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <hr />
                <h4 className="mt-4">OrderPageType</h4>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Namn *</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatusData.orderPageType?.name ?? ""}
                                onChange={e =>
                                    setOrderStatusData({
                                        ...orderStatusData,
                                        orderPageType: {
                                            ...orderStatusData.orderPageType,
                                            name: e.currentTarget.value,
                                        } as OrderPageType,
                                    })
                                }
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Beskrivning</Form.Label>
                            <Form.Control
                                type="text"
                                value={orderStatusData.orderPageType?.description ?? ""}
                                onChange={e =>
                                    setOrderStatusData({
                                        ...orderStatusData,
                                        orderPageType: {
                                            ...orderStatusData.orderPageType,
                                            description: e.currentTarget.value,
                                        } as OrderPageType,
                                    })
                                }
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    {[
                        "customer", "assignedTo", "registrationNumber", "vin", "carInfo", "aoNumber", "externalOrderId", "foNumber", "ioNumber", "orderType",
                        "orderedBy", "followUp", "emailBody", "warehouseLocation", "supplier", "arriveDate", "bookingDate", "deliveryDate", "pickupTime",
                        "orderDate", "fileName", "comment", "internalComment", "carUrl",
                        "articleTableQuantity", "articleTableDescription", "articleTableArticleNumber", "articleTablePurchasePrice", "articleTableSalesPrice",
                        "articleTableSupplierName", "articleTableIONumber", "articleTableComment", "articleTableDiscount", "articleTablePartUrl", "articleTableIsSyncedWithAX",
                        "emailPriceProposal"
                    ].map((field) => (
                        <Col xs={12} md={4} key={field}>
                            <Form.Group className="mb-3">
                                <Form.Label>{field}</Form.Label>
                                <div>
                                    <Form.Check
                                        type="switch"
                                        id={field}
                                        label={booleanLabel((orderStatusData.orderPageType as any)?.[field])}
                                        checked={!!(orderStatusData.orderPageType as any)?.[field]}
                                        onChange={e =>
                                            setOrderStatusData({
                                                ...orderStatusData,
                                                orderPageType: {
                                                    ...orderStatusData.orderPageType,
                                                    [field]: e.currentTarget.checked,
                                                } as OrderPageType,
                                            })
                                        }
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                    ))}
                </Row>
                <div className="text-end">
                    <Button
                        variant="primary"
                        className="mb-3"
                        onClick={handleAddOrderStatus}
                        disabled={!orderStatusData.name || !orderStatusData.orderPageType || !orderStatusData.orderPageType.name || isWaitingOrderStatusPostResponse}
                    >
                        Lägg till orderstatus
                    </Button>
                </div>

                <Alert key="alert" variant="danger" show={displayError}>
                    <p>Något gick fel, kontrollera uppgifterna och försök igen.</p>
                </Alert>
            </Form>
        </div>
    );
};

export default OrderStatusAdd;
